import { useState, useCallback } from "react";
import { callExtendedService } from "@/api/extendedServices";
import { ExtendedServicePayload } from "@/types/extendedViewService";

export const useExtendedServicePopup = () => {
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
    {}
  );

  const openExtendedService = useCallback(
    async (
      userId: string,
      receiptId: string,
      payload: ExtendedServicePayload,
      itemKey: string = "default"
    ): Promise<void> => {
      // Open popup immediately on user click to avoid popup blockers.
      // Browsers allow popups when called synchronously from user interaction.
      // This pattern works across all modern browsers (Chrome, Firefox, Safari, Edge)
      // because window.open() is invoked directly in the user's click event handler,
      // not after an async operation completes. We open a blank window first, then
      // navigate it to the final URL after the API call completes.
      const popup = window.open("", "_blank");

      if (!popup) {
        console.error("Popup blocked by browser");
        return;
      }

      popup.opener = null;

      setLoadingStates((prev) => ({ ...prev, [itemKey]: true }));
      try {
        const response = await callExtendedService(userId, receiptId, payload);

        if (response.url) {
          popup.location.href = response.url;
        } else {
          popup.close();
        }
      } catch (error) {
        console.error("Error calling extended service:", error);
        popup.close();
      } finally {
        setLoadingStates((prev) => ({ ...prev, [itemKey]: false }));
      }
    },
    []
  );

  const isLoading = (itemKey: string = "default") => {
    return loadingStates[itemKey] ?? false;
  };

  return { openExtendedService, isLoading };
};
